import { IonModal, IonSpinner } from '@ionic/react';
import type { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { type CSSProperties, Suspense, lazy, useEffect, useRef, useState } from 'react';

import { readCodedError } from '~/api/codedError';
import type { CompanyDocument, DocumentFile } from '~/api/documents';
import { useDocumentFileQuery } from '~/api/hooks/useDocuments';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { Icon } from '~/components/ui/Icon';
import { saveBlob } from '~/lib/download';
import { GuideBand } from '~/pages/impostos/GuideCard';

// pdf.js é pesado (~1,4 MB) e só é necessário ao abrir um documento, como no detalhe da nota.
const PdfPreview = lazy(() =>
  import('~/components/ui/PdfPreview').then((module) => ({ default: module.PdfPreview })),
);

const PDF_CONTENT_TYPE: CompanyDocument['content_type'] = 'application/pdf';

const FILE_UNAVAILABLE = 'Não foi possível abrir o documento. Confira a conexão e tente de novo.';
const PREVIEW_FAILURE =
  'Não foi possível mostrar a prévia deste documento no aparelho. Use "Abrir" ou "Baixar".';

const SHEET_STYLE: CSSProperties = {
  padding: 24,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const HEADER_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  paddingBottom: 16,
  borderBottom: '0.5px solid var(--rx-border)',
};

const OVERLINE_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: 'var(--rx-text-3)',
};

const TITLE_STYLE: CSSProperties = {
  margin: '4px 0 0',
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: '-0.4px',
  color: 'var(--rx-text)',
};

const BODY_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  marginTop: 16,
  background: 'var(--rx-surface)',
  border: '0.5px solid var(--rx-border)',
  borderRadius: 14,
};

const CENTERED_STYLE: CSSProperties = { padding: 36, display: 'flex', justifyContent: 'center' };

const IMAGE_STYLE: CSSProperties = { display: 'block', maxWidth: '100%', margin: '0 auto' };

const ACTIONS_STYLE: CSSProperties = { display: 'flex', gap: 8, paddingTop: 14 };

const ACTION_STYLE: CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '12px 12px',
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  fontFamily: 'inherit',
};

const OPEN_STYLE: CSSProperties = {
  ...ACTION_STYLE,
  background: 'var(--rx-surface)',
  border: '0.5px solid var(--rx-border)',
  color: 'var(--rx-text)',
};

const DOWNLOAD_STYLE: CSSProperties = {
  ...ACTION_STYLE,
  background: 'var(--rx-brand)',
  border: 'none',
  color: 'var(--rx-brand-ink)',
};

const UNAVAILABLE_ACTION_STYLE: CSSProperties = { opacity: 0.5, cursor: 'not-allowed' };

interface DocumentoSheetProps {
  /** O documento aberto; `null` mantém a folha fechada. */
  companyDocument: CompanyDocument | null;
  /** A empresa em operação: a URL assinada só sai com ela. */
  clientId: number;
  /** Chamado depois que a folha fechou: "Fechar", o gesto de arrastar ou o voltar do aparelho. */
  onClose(): void;
}

/**
 * O visualizador de um documento, numa folha sobre a tela que o abriu (o feed, as pastas ou
 * "Impostos"). Pede a URL assinada ao abrir, baixa o arquivo e mostra a prévia do blob: o PDF pelo
 * pdf.js e a imagem num `<img>` (todo documento da E14 vira PDF; a imagem é defesa). "Abrir" e
 * "Baixar" usam o mesmo blob, que é revogado quando a folha fecha. Na guia (E16), a faixa dela
 * fica acima da prévia: valor, vencimento, situação, os dois "Copiar" e o "Paguei" ou "Desfazer".
 */
export function DocumentoSheet({
  companyDocument,
  clientId,
  onClose,
}: DocumentoSheetProps): JSX.Element {
  const modalRef = useRef<HTMLIonModalElement>(null);

  return (
    <IonModal ref={modalRef} isOpen={companyDocument !== null} onDidDismiss={onClose}>
      {companyDocument && (
        <DocumentViewer
          companyDocument={companyDocument}
          clientId={clientId}
          onClose={() => void modalRef.current?.dismiss()}
        />
      )}
    </IonModal>
  );
}

/**
 * Um object URL do blob enquanto ele existir; o anterior é revogado quando o blob muda e quando
 * a tela desmonta, que é o fechamento da folha.
 */
function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [blob]);

  return url;
}

/** A recusa da API (o arquivo que sumiu, o documento excluído) diz o motivo; o resto é a rede. */
function fileFailureMessage(error: Error): string {
  const coded = error instanceof AxiosError ? readCodedError(error.response?.data) : null;
  return coded?.message || FILE_UNAVAILABLE;
}

interface DocumentViewerProps {
  companyDocument: CompanyDocument;
  clientId: number;
  onClose(): void;
}

function DocumentViewer({ companyDocument, clientId, onClose }: DocumentViewerProps): JSX.Element {
  const file = useDocumentFileQuery(clientId, companyDocument.id);
  const fileUrl = useObjectUrl(file.data?.blob);

  return (
    <div className="rx-container rx-screen" style={SHEET_STYLE}>
      <header style={HEADER_STYLE}>
        <div style={{ minWidth: 0 }}>
          <div style={OVERLINE_STYLE}>{companyDocument.section_label}</div>
          <h2 style={TITLE_STYLE}>{companyDocument.title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Fechar" className="rx-iconbtn">
          <Icon name="back" size={18} />
        </button>
      </header>

      <GuideBand companyDocument={companyDocument} clientId={clientId} />

      <div style={BODY_STYLE}>
        <FilePreview companyDocument={companyDocument} file={file} fileUrl={fileUrl} />
      </div>

      <FileActions file={file.data} fileUrl={fileUrl} />
    </div>
  );
}

interface FilePreviewProps {
  companyDocument: CompanyDocument;
  file: UseQueryResult<DocumentFile>;
  fileUrl: string | null;
}

function FilePreview({ companyDocument, file, fileUrl }: FilePreviewProps): JSX.Element {
  if (file.isError) {
    return (
      <FileFailure
        message={fileFailureMessage(file.error)}
        retrying={file.isFetching}
        onRetry={() => void file.refetch()}
      />
    );
  }
  if (!fileUrl) {
    return <Loading label="Carregando o documento" />;
  }
  if (companyDocument.content_type !== PDF_CONTENT_TYPE) {
    return <img src={fileUrl} alt={companyDocument.title} style={IMAGE_STYLE} />;
  }
  return (
    <ErrorBoundary key={fileUrl} fallback={renderPreviewFailure}>
      <Suspense fallback={<Loading label="Preparando a prévia" />}>
        <PdfPreview fileUrl={fileUrl} label={companyDocument.title} />
      </Suspense>
    </ErrorBoundary>
  );
}

// A prévia tem a própria boundary: uma falha do pdf.js (navegador antigo, PDF que ele não lê)
// fica na área da prévia, e "Abrir" e "Baixar" continuam na folha.
function renderPreviewFailure(): JSX.Element {
  return <Warning message={PREVIEW_FAILURE} />;
}

function Loading({ label }: { label: string }): JSX.Element {
  return (
    <output aria-label={label} style={CENTERED_STYLE}>
      <IonSpinner name="dots" />
    </output>
  );
}

function Warning({ message }: { message: string }): JSX.Element {
  return (
    <div role="alert" style={{ padding: '14px 16px', display: 'flex', gap: 10 }}>
      <span aria-hidden="true">
        <Icon name="warn" size={16} color="var(--rx-negative)" />
      </span>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--rx-text)' }}>{message}</p>
    </div>
  );
}

interface FileFailureProps {
  message: string;
  retrying: boolean;
  onRetry(): void;
}

function FileFailure({ message, retrying, onRetry }: FileFailureProps): JSX.Element {
  return (
    <div>
      <Warning message={message} />
      <div style={{ padding: '0 16px 16px' }}>
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          style={{ ...OPEN_STYLE, width: '100%' }}
        >
          <span aria-hidden="true">
            <Icon name="refresh" size={14} />
          </span>
          Tentar de novo
        </button>
      </div>
    </div>
  );
}

interface FileActionsProps {
  file: DocumentFile | undefined;
  fileUrl: string | null;
}

function FileActions({ file, fileUrl }: FileActionsProps): JSX.Element {
  return (
    <div style={ACTIONS_STYLE}>
      {fileUrl ? (
        <a href={fileUrl} target="_blank" rel="noreferrer noopener" style={OPEN_STYLE}>
          Abrir
        </a>
      ) : (
        <button type="button" disabled style={{ ...OPEN_STYLE, ...UNAVAILABLE_ACTION_STYLE }}>
          Abrir
        </button>
      )}
      <button
        type="button"
        disabled={!file}
        onClick={() => file && saveBlob(file.blob, file.filename)}
        style={file ? DOWNLOAD_STYLE : { ...DOWNLOAD_STYLE, ...UNAVAILABLE_ACTION_STYLE }}
      >
        <span aria-hidden="true">
          <Icon name="download" size={14} />
        </span>
        Baixar
      </button>
    </div>
  );
}
